import dayjs from "dayjs";

/** Normalize academic year labels to "YYYY-YYYY" (e.g. 2025-26 → 2025-2026). */
export function normalizeAcademicYear(ay) {
	if (!ay) return "";
	const s = String(ay).trim();
	const m = s.match(/^(\d{4})\s*[-/]\s*(\d{2,4})$/);
	if (!m) return s;
	const y1 = parseInt(m[1], 10);
	let y2 = parseInt(m[2], 10);
	if (m[2].length === 2) {
		const century = Math.floor(y1 / 100) * 100;
		y2 = y2 < 50 ? century + 100 + y2 : century + y2;
		if (y2 <= y1) y2 = y1 + 1;
	}
	return `${y1}-${y2}`;
}

/** Indian academic year: 1 Apr (y1) – 31 Mar (y2). */
export function getAcademicYearRange(ay) {
	const norm = normalizeAcademicYear(ay);
	const m = norm.match(/^(\d{4})-(\d{4})$/);
	if (!m) {
		const inferred = inferAcademicYearFromDate(dayjs());
		return getAcademicYearRange(inferred);
	}
	const y1 = parseInt(m[1], 10);
	const y2 = parseInt(m[2], 10);
	return {
		start: dayjs(`${y1}-04-01`).startOf("day"),
		end: dayjs(`${y2}-03-31`).endOf("day"),
		label: norm,
	};
}

/** April–March academic year for a calendar date. */
export function inferAcademicYearFromDate(date) {
	const d = dayjs(date);
	if (!d.isValid()) return "";
	const month = d.month() + 1;
	const year = d.year();
	if (month >= 4) return `${year}-${year + 1}`;
	return `${year - 1}-${year}`;
}

/** Whether a teacher_schedules row overlaps the academic year (Apr–Mar). */
export function scheduleOverlapsAcademicYear(schedule, academicYear) {
	if (!schedule?.start_date) return false;

	const range =
		typeof academicYear === "string"
			? getAcademicYearRange(academicYear)
			: academicYear;
	if (!range?.start || !range?.end) return true;

	const scheduleStart = dayjs(schedule.start_date).startOf("day");
	const scheduleEnd = schedule.end_date
		? dayjs(schedule.end_date).endOf("day")
		: range.end;

	return (
		!scheduleEnd.isBefore(range.start) && !scheduleStart.isAfter(range.end)
	);
}

/** Report settings up to a period, scoped to an academic year (Apr–Mar). */
export function filterReportSettingsForCumulative(
	settings,
	primaryMonth,
	primaryYear,
	academicYear
) {
	return settings.filter((setting) => {
		if (setting.status === false) return false;
		if (!monthYearInAcademicYear(setting.month, setting.year, academicYear)) {
			return false;
		}
		if (setting.year < primaryYear) return true;
		if (setting.year === primaryYear && setting.month <= primaryMonth) {
			return true;
		}
		return false;
	});
}

/** Whether a calendar month (1–12) and year fall within an academic year (Apr–Mar). */
export function monthYearInAcademicYear(month, year, academicYear) {
	const range = getAcademicYearRange(academicYear);
	if (!range?.start || !range?.end) return true;

	const monthNum = Number(month);
	const yearNum = Number(year);
	if (!Number.isFinite(monthNum) || !Number.isFinite(yearNum)) return false;

	const d = dayjs(`${yearNum}-${String(monthNum).padStart(2, "0")}-01`);
	return (
		!d.isBefore(range.start, "month") && !d.isAfter(range.end, "month")
	);
}

export function visitMatchesAcademicYear(visit, academicYear) {
	const target = normalizeAcademicYear(academicYear);
	if (!target) return true;

	if (visit?.academic_year) {
		return normalizeAcademicYear(visit.academic_year) === target;
	}

	const date =
		visit?.planned_date || visit?.visited_date || visit?.created_at || null;
	return inferAcademicYearFromDate(date) === target;
}
